import type { TableMasterResponse } from '@/lib/api/client/types.gen'

export const downloadCSVTemplate = (filename: string, headers: string[]) => {
  const csvContent = headers.join(',') + '\n'
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
  const link = document.createElement('a')
  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute('download', filename)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }
}

export const GUEST_IMPORT_TEMPLATE_HEADERS = [
  'fullName',
  'displayName',
  'group',
  'side',
  'followers',
  'lineUserId',
  'maritalStatus',
  'tableNumber',
]

export const TABLE_IMPORT_TEMPLATE_HEADERS = [
  'tableNumber',
  'capacity',
  'notes',
]

export const LINE_MAP_IMPORT_TEMPLATE_HEADERS = [
  'lineUserId',
  'fullName',
  'displayName',
  'maritalStatus',
]

type ExportableGuest = {
  fullName: string
  displayName?: string | null
  side?: 'groom' | 'bride' | null
  group?: string | null
  expectedFollowers?: number
  phone?: string | null
  notes?: string | null
  status?: string
  checkedInAt?: string | null
  lineUserIds?: string[] | null
  preloadedUsers?: Record<
    string,
    { displayName?: string; maritalStatus?: string }
  > | null
  tableAssignment?: {
    id: string
    tableId: string
    tableNumber: string
    zone?: string | null
  } | null
}

const escapeCSV = (value: unknown): string => {
  if (value === null || value === undefined) return ''
  const str = String(value)
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`
  }
  return str
}

export const downloadGuestsCSV = (
  guests: ExportableGuest[],
  filename = 'guests_export.csv',
) => {
  const headers = [
    'fullName',
    'displayName',
    'group',
    'side',
    'followers',
    'lineUserId',
    'maritalStatus',
    'tableNumber',
    'status',
    'checkedInAt',
  ]

  const rows = guests.map((g) => {
    const lineUserId = g.lineUserIds?.[0] || ''
    const preloadedUser = lineUserId ? g.preloadedUsers?.[lineUserId] : null
    const maritalStatus = preloadedUser?.maritalStatus || ''
    const tableNumber = g.tableAssignment?.tableNumber || ''

    return [
      g.fullName,
      g.displayName || '',
      g.group || '',
      g.side || '',
      g.expectedFollowers ?? 1,
      lineUserId,
      maritalStatus,
      tableNumber,
      g.status || '',
      g.checkedInAt || '',
    ]
      .map(escapeCSV)
      .join(',')
  })

  const csvContent = [headers.join(','), ...rows].join('\n')
  const blob = new Blob(['\ufeff' + csvContent], {
    type: 'text/csv;charset=utf-8;',
  })
  const link = document.createElement('a')
  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute('download', filename)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }
}

export const downloadTablesCSV = (
  tables: TableMasterResponse[],
  filename = 'tables_export.csv',
) => {
  const headers = [
    'tableNumber',
    'capacity',
    'notes',
    'x',
    'y',
    'type',
    'shape',
    'width',
    'height',
  ]

  const rows = tables.map((t) => {
    return [
      t.tableNumber,
      t.capacity ?? '',
      t.notes || '',
      t.x ?? '',
      t.y ?? '',
      t.type || 'table',
      t.shape || 'circle',
      t.width ?? '',
      t.height ?? '',
    ]
      .map(escapeCSV)
      .join(',')
  })

  const csvContent = [headers.join(','), ...rows].join('\n')
  const blob = new Blob(['\ufeff' + csvContent], {
    type: 'text/csv;charset=utf-8;',
  })
  const link = document.createElement('a')
  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute('download', filename)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }
}
